import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Trophy,
  DollarSign,
  Users,
  Clock,
  Shield,
  FileText,
  AlertCircle,
  CheckCircle,
  Banknote,
  Scale,
  HelpCircle
} from 'lucide-react';
import { Button } from './ui/button';

const TournamentRulesPage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'eligibility',
      title: 'Eligibility',
      icon: Users,
      content: [
        'Must be 18 years of age or older',
        'Must be a registered Par Seeker user',
        'Must have a valid payment method on file',
        'Must have a connected payout account (bank or debit card) to receive prizes',
        'Must be located in a jurisdiction where skill-based gaming is permitted'
      ]
    },
    {
      id: 'entry-fees',
      title: 'Entry Fees',
      icon: DollarSign,
      content: [
        'Entry fees are collected at the time of tournament registration',
        'Entry fees are non-refundable once the tournament begins',
        'If a tournament is cancelled before play begins, entry fees are fully refunded',
        'Entry fees are processed securely via Stripe',
        'You may use your existing prize balance to pay entry fees'
      ]
    },
    {
      id: 'prize-pool',
      title: 'Prize Pool',
      icon: Trophy,
      content: [
        'Prize pool equals total entry fees minus the platform fee',
        'Platform fee: 10% of total entry fees (minimum $1.00)',
        'Prize pool is distributed to winners based on tournament format:',
        '• Skins: Prizes divided proportionally by skins won',
        '• Match Play: Winner takes the prize pool (ties split)',
        '• Stroke Play: Paid positions based on field size'
      ]
    },
    {
      id: 'payouts',
      title: 'Prize Distribution & Payouts',
      icon: Banknote,
      content: [
        'Prizes are credited to your Par Seeker prize balance within 24 hours of tournament completion',
        'Minimum withdrawal amount: $10.00',
        'Standard payout: 2-3 business days (free)',
        'Instant payout: Within minutes (1.5% fee)',
        'Payouts are processed via Stripe to your connected bank account or debit card',
        'You are responsible for ensuring your payout account information is accurate'
      ]
    },
    {
      id: 'completion',
      title: 'Tournament Completion',
      icon: CheckCircle,
      content: [
        'A tournament is considered final when the host marks it complete AND all scores are verified',
        'There is a 24-hour dispute window after tournament completion',
        'If disputes remain unresolved, the tournament may be voided and entry fees refunded',
        'All players must attest to their scores for the tournament to be finalized'
      ]
    },
    {
      id: 'scoring',
      title: 'Scoring & Rules of Play',
      icon: FileText,
      content: [
        'USGA Rules of Golf apply unless otherwise specified by the tournament host',
        'Handicaps are applied per tournament settings (gross or net play)',
        'Scores must be attested by at least one other player in the group',
        'Scorecards may be audited at any time',
        'Falsified scores result in immediate disqualification and forfeiture of entry fee',
        'Repeated violations may result in account suspension'
      ]
    },
    {
      id: 'disputes',
      title: 'Disputes',
      icon: Scale,
      content: [
        'Disputes must be raised within 24 hours of tournament completion',
        'The tournament host has initial resolution authority',
        'Unresolved disputes can be escalated to Par Seeker support',
        'Par Seeker\'s decision on disputes is final',
        'Evidence (photos, witnesses) should be provided when filing disputes'
      ]
    },
    {
      id: 'cancellations',
      title: 'Cancellations & Refunds',
      icon: Clock,
      content: [
        'Tournament host may cancel a tournament before play begins (full refunds issued)',
        'Once play begins, tournaments cannot be cancelled except by unanimous player consent',
        'In case of inclement weather, the host decides whether to postpone or cancel',
        'Refunds for cancelled tournaments are processed within 5-7 business days',
        'No refunds are given for player withdrawal after tournament start'
      ]
    },
    {
      id: 'taxes',
      title: 'Tax Responsibility',
      icon: FileText,
      content: [
        'Tournament winnings are considered taxable income in the United States',
        'You are responsible for reporting all winnings on your tax return',
        'Par Seeker will issue IRS Form 1099-MISC to users receiving $600 or more in a calendar year',
        'Entry fees may be deductible as gambling losses up to the amount of winnings',
        'Consult a qualified tax professional for personalized advice',
        'Tax documents are available in your account settings'
      ]
    },
    {
      id: 'platform-rights',
      title: 'Platform Rights',
      icon: Shield,
      content: [
        'Par Seeker reserves the right to void tournaments with evidence of collusion or fraud',
        'Accounts may be suspended or terminated for violations of these rules',
        'Platform fee percentage is non-negotiable',
        'Par Seeker may modify these rules at any time with reasonable notice',
        'Continued use of tournament features constitutes acceptance of any rule changes'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[var(--ps-bg-surface)] border-b border-[var(--ps-border)]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[var(--ps-text-primary)]">Tournament Rules</h1>
            <p className="text-xs text-[var(--ps-text-muted)]">Prize Policy & Terms</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Intro */}
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-6 mb-8 border border-green-500/20">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">
                Par Seeker Tournament Rules & Prize Policy
              </h2>
              <p className="text-sm text-[var(--ps-text-muted)]">
                All Par Seeker cash games are skill-based tournaments with prize payouts. 
                By entering a tournament, you agree to these rules.
              </p>
              <p className="text-xs text-[var(--ps-text-muted)] mt-2">
                Last Updated: July 2026
              </p>
            </div>
          </div>
        </div>

        {/* Quick Summary */}
        <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-5 mb-8">
          <h3 className="font-semibold text-[var(--ps-text-primary)] mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[var(--ps-primary)]" />
            Quick Summary
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-[var(--ps-text-muted)]">10% platform fee on prize pools</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-[var(--ps-text-muted)]">$10 minimum withdrawal</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-[var(--ps-text-muted)]">24-hour dispute window</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-[var(--ps-text-muted)]">1099 issued at $600+/year</span>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <div 
              key={section.id}
              id={section.id}
              className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] overflow-hidden"
            >
              <div className="p-4 border-b border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[var(--ps-primary)]/10 flex items-center justify-center">
                    <section.icon className="w-4 h-4 text-[var(--ps-primary)]" />
                  </div>
                  <h3 className="font-semibold text-[var(--ps-text-primary)]">
                    {index + 1}. {section.title}
                  </h3>
                </div>
              </div>
              <div className="p-4">
                <ul className="space-y-2">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[var(--ps-text-muted)]">
                      {item.startsWith('•') ? (
                        <span className="ml-4">{item}</span>
                      ) : (
                        <>
                          <span className="text-green-500 mt-1">•</span>
                          <span>{item}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Notice */}
        <div className="mt-8 p-4 bg-[var(--ps-bg-elevated)] rounded-xl border border-[var(--ps-border)]">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[var(--ps-text-muted)]">
              <p className="font-medium text-[var(--ps-text-primary)] mb-1">Important Notice</p>
              <p>
                Par Seeker tournament games are skill-based competitions, not gambling. 
                Golf is a game of skill where practice, technique, and strategy determine outcomes. 
                By participating, you acknowledge that your performance is based on your own abilities.
              </p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-6 text-center text-sm text-[var(--ps-text-muted)]">
          <p>Questions about these rules?</p>
          <a 
            href="mailto:support@parseeker.com" 
            className="text-[var(--ps-primary)] hover:underline"
          >
            Contact Support
          </a>
        </div>

        {/* Bottom Padding */}
        <div className="h-20"></div>
      </div>
    </div>
  );
};

export default TournamentRulesPage;
